import { Injectable, OnDestroy, Optional } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, forkJoin, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';

import { UsersApi } from '../../../api/users/users.api';
import { UserStateApiModel } from '../../../api/users/models/user-state.api.model';
import { AccountsApi } from '../../../api/accounts/accounts.api';
import { AccountApiModel } from '../../../api/accounts/models/account.api.model';
import { TargetingPreferencesApi } from '../../../api/targeting/targeting-preferences.api';
import { TargetingAccountPreferencesApiModel } from '../../../api/targeting/model/targeting-account-preferences.api.model';

import { SessionManagementService, SessionStateEnum } from '../../../Bootstrap/services/session-management.service';
import { UserCurrency } from '../enums/user-currency';

import { UserModel } from '../models/user.model';
import { AccountModel } from '../models/account.model';
import { UserStateModel } from '../models/user-state.model';
import { userPermissionsEnum } from '../enums/user-permissions.enum';
import { PreferencesModel } from '../models/preferences.model';
import { LanguageTagEnum } from '../../Localization/enums/language-tag.enum';
import { LoginApi } from '../../../api/login/login.api';
import { LoginSuccessResponse } from '../../../api/login/login-response';
import { WebsocketAwareNeptuneService } from '../../../Bootstrap/services/websocket-aware-neptune.service';
import { HttpVerbsEnum } from '../../../api/shared/enums/http-verbs.enum';
import { UserAccountModel } from '../models/user-account.model';
import { UserStateProvider } from '../interface/user-state-provider.interface';

export enum TargetingAccessLevel {
    NO_ACCESS,
    CLASSIC_ONLY,
    ADVANCED
}

@Injectable()
export class CurrentUserStateService implements OnDestroy, UserStateProvider {

    private state: UserStateModel = null;
    private stateProvider: BehaviorSubject<UserStateModel> = new BehaviorSubject<UserStateModel>(this.state);
    private stateRequest: Subscription;

    private allowClassicRedirect: boolean = true;

    private ignoreRedirectRoutes = ['/app/login'];
    private subscriptions: Subscription[] = [];

    public ignoreWebsockets: boolean = false;

    // @TODO: Remove this, this is a hack to allow greater testing on prod
    readonly aiHelperIds: number[] = [51, 1210, 24395, 24396, 54863, 2040, 2041, 1867];
    readonly customerAiHelperIds: number[] = [30719, 61191, 137, 890, 59389, 37486, 9923, 57135, 49120, 39582];

    constructor(
        private usersApi: UsersApi,
        private accountsApi: AccountsApi,
        private loginApi: LoginApi,
        private targetingPrefsApi: TargetingPreferencesApi,
        private sessionManagementService: SessionManagementService,
        private router: Router,
        @Optional() private websocketAwareNeptuneService: WebsocketAwareNeptuneService
    ) {
        this.subscriptions.push(
            this.sessionManagementService.userSessionExpired$.pipe(
                switchMap(({performLogout}) => {
                    if (performLogout) {
                        return this.loginApi.performLogout('SessionExpired').pipe(
                            tap(done => {
                                try {
                                    this.websocketAwareNeptuneService.sendWebsocketEventInstant('/login', HttpVerbsEnum.DELETE);
                                } catch (err) {
                                    console.error('Could not close login websocket', err);
                                }
                            }),
                            catchError((err) => {
                                console.log('logout', err);
                                return of(null);
                            }),
                        );
                    }
                    return of(null);
                }),

            ).subscribe(),
            this.sessionManagementService.getUserIsLoggedOut$().pipe(
                tap(() => {
                    this.clearState();
                    this.router.navigate(['login']);
                })
            ).subscribe(),
            this.sessionManagementService.heartbeatCall$.pipe(
                switchMap(() => this.usersApi.getCurrentUserState().pipe(
                    catchError(() => of(null))
                ))
            ).subscribe()
        );

        if (this.websocketAwareNeptuneService) {
            this.subscriptions.push(
                this.websocketAwareNeptuneService.getWebsocketMessages().subscribe(
                    (message) => {
                        if (message?.resourceType === 'login' && !this.ignoreWebsockets) {
                            this.loadUserState();
                        }
                    }
                )
            );
        }
    }

    loadUserState$(): Observable<any> {
        return forkJoin([this.usersApi.getCurrentUserState(), this.accountsApi.getCurrentUserAccounts(),this.usersApi.getAllUsersFromCurrentAccount()]).pipe(
            map(([userState, accountState, accountUsersState]) => {
                return {
                    userState: userState,
                    accountState: accountState,
                    accountUsersState: accountUsersState,
                };
            }),
            tap((({userState, accountState, accountUsersState}) => {
                if (this.state && (
                    this.state?.account?.id !== userState.currentUserAccount.account.id ||
                    this.state?.user?.id !== userState.currentUserAccount.user.id)
                ){
                    window.location.reload();
                }

                this.setStateFromApi(userState, accountState, accountUsersState);
            }))
        );
    }

    combinedWithATState$(): Observable<any> {
        return this.loadUserState$().pipe(
            mergeMap((state) => {
                if (state?.userState?.currentUserAccount?.permissions.includes(userPermissionsEnum.advancedTargeting)) {
                    return this.targetingPrefsApi.getOwnAccountPreferences();
                } else {
                    this.stateRequest = null;
                    return of(null);
                }
            }),
            tap(((prefs) => {
                this.stateRequest = null;
                this.setStateAfterTargeting(prefs);
            })),
            catchError(error => {
                this.stateRequest = null;
                throw error;
            })
        );
    }

    /**
    * Loads the user state from the server
    */
    public loadUserState(): void {
        this.stateRequest = this.combinedWithATState$().subscribe();
    }

    public startGhostIn(): void {
        this.allowClassicRedirect = false;
    }

    public logout(reason: string, navigateToLogin = true): Observable<boolean> {
        return this.loginApi.performLogout(reason).pipe(
            map(done => {
                this.clearState();
                this.websocketAwareNeptuneService.sendWebsocketEventInstant('/login', HttpVerbsEnum.DELETE);
                if (navigateToLogin) {
                    this.router.navigate(['Login']);
                }
                this.sessionManagementService.setSessionState(SessionStateEnum.UNAUTHENTICATED);
                return false;
            }),
            catchError((error) => {
                console.log('Couldn\'t log out');
                if (error?.code === 401) {
                    this.clearState();
                    this.router.navigate(['Login']);
                }
                this.sessionManagementService.setSessionState(SessionStateEnum.UNAUTHENTICATED);
                return of(false);
            })
        );
    }

    public switchAccount(accountId: number): Observable<LoginSuccessResponse> {
        return this.loginApi.switchAccount(accountId).pipe(
            tap((ssoResponse) => {
                if (this.websocketAwareNeptuneService) {
                    this.ignoreWebsockets = true;
                    this.websocketAwareNeptuneService.sendWebsocketEventInstant('/login/switchAccount', HttpVerbsEnum.POST);
                }
            })
        );
    }

    clearState() {
        this.setState(null);
    }

    /**
    * Gets the full user state
    */
    public getState(): Observable<UserStateModel> {
        if (this.state == null && !this.stateRequest) {
            this.loadUserState();
        }
        return this.stateProvider;
    }

    /**
    * Gets the full current user individually
    * without other state information (preferences, accounts, etc)
    */
    public getUser(): Observable<UserModel> {
        return this.getState().pipe(map(state => {
            if (!state) {
                return null;
            }
            return state.user;
        }));
    }

    /**
    * Gets the list of accounts associated with the current user
    */
    public getAccounts(): Observable<AccountModel[]> {
        return this.getState().pipe(map(state => {
            if (!state) {
                return null;
            }
            return state.accounts;
        }));
    }

    /**
    * Gets the current user account individually
    */
    public getAccount(): Observable<AccountModel> {
        return this.getState().pipe(map(state => {
            if (!state) {
                return null;
            }
            return state.account;
        }));
    }

    /**
    * Gets the current user's permissions
    */
    public getPermissions(): Observable<userPermissionsEnum[]> {
        return this.getState().pipe(map(state => {
            if (!state) {
                return null;
            }
            return state.permissions;
        }));
    }

    /**
    * Gets the current account users
    */
    public getAccountUsers(): Observable<UserModel[]> {
        return this.getState().pipe(map(state => {
            if (!state) {
                return null;
            }
            return state.account.users;
        }));
    }

    /**

    /**
    * Gets the current user's preferences
    */
    public getPreferences(): Observable<PreferencesModel> {
        return this.getState().pipe(map(state => {
            if (!state) {
                return null;
            }
            return state.preferences;
        }));
    }

    public getUILanguageTag(): Observable<string> {
        return this.getState().pipe(map(state => {
            if (!state && navigator.language) {
                // not login state: browser language
                const navigatorLanguage = navigator.language as LanguageTagEnum;
                if (Object.values(LanguageTagEnum).includes(navigatorLanguage)) {
                    return navigatorLanguage;
                } else {
                    // default: English
                    return LanguageTagEnum.en_US;
                }
            }
            // Login state: user preference language
            return state.preferences.uiLanguage.languageTag;
        }));
    }

    public getDataReferenceLanguageTag(): Observable<string> {
        return this.getState().pipe(map(state => {
            if (!state) {
                return null;
            }
            return state.preferences.referenceDataLanguage.languageTag;
        }));
    }

    public getUserCurrency(): Observable<UserCurrency>{
        return this.getState().pipe(map(state => {
            if (!state) {
                return null;
            }
            return state.preferences.currency;
        }));
    }

    /**
    * Gets the full current impersonator user individually
    * without other state information (preferences, accounts, etc)
    */
    public getImpersonatorUser(): Observable<UserModel> {
        return this.getState().pipe(map(state => {
            if (!state || !state.impersonator) {
                return null;
            }
            return state.impersonator.user;
        }));
    }

    /**
    * Gets the current impersonator account individually
    */
    public getImpersonatorAccount(): Observable<AccountModel> {
        return this.getState().pipe(map(state => {
            if (!state || !state.impersonator) {
                return null;
            }
            return state.impersonator.account;
        }));
    }

    /**
    * Gets the current impersonator's permissions
    */
    public getImpersonatorPermissions(): Observable<userPermissionsEnum[]> {
        return this.getState().pipe(map(state => {
            if (!state || !state.impersonator) {
                return null;
            }
            return state.impersonator.permissions;
        }));
    }

    /**
    * Gets the current impersonator preferences
    */
    public getImpersonatorPreferences(): Observable<PreferencesModel> {
        return this.getState().pipe(map(state => {
            if (!state || !state.impersonator) {
                return null;
            }
            return state.impersonator.preferences;
        }));
    }

    public getTargetingAccessLevel(): Observable<TargetingAccessLevel> {
        return this.getPermissions().pipe(
            filter(x => !!x),
            map(permissions => {
                if (!!permissions.find(x => x === userPermissionsEnum.advancedTargeting)) {
                    return TargetingAccessLevel.ADVANCED;
                } else if (!!permissions.find(y => y === userPermissionsEnum.targeting)) {
                    return TargetingAccessLevel.CLASSIC_ONLY;
                } else {
                    return TargetingAccessLevel.NO_ACCESS;
                }
            })
        );
    }

    /**
    * Sets the user state based on
    * the results of an API call
    */
    private setStateFromApi(
        stateApiEntity: UserStateApiModel,
        accountsApiEntities: AccountApiModel[],
        accountUsersEntity: UserAccountModel[]
    ): void {
        const state = new UserStateModel(stateApiEntity, accountsApiEntities, accountUsersEntity);

        this.sessionManagementService.setSessionState(SessionStateEnum.ACTIVE);
        this.setState(state);
    }

    /**
    * Sets the user state based on the targeting preferences (only sets influencers streams permissions)
    */
    private setStateAfterTargeting(
        targetingPreferences: TargetingAccountPreferencesApiModel
    ): void {
        if (targetingPreferences?.hasInfluencerStreams) {
            this.state.permissions.push(userPermissionsEnum.influencerStreams);
            this.setState(this.state);
        }
    }

    /**
    * Sets the full user state
    */
    private setState(newValue: UserStateModel): void {
        this.state = newValue;
        this.stateProvider.next(newValue);
    }

    acceptTos(): Observable<null> {
        this.state.preferences.tosAccepted = true;
        return this.usersApi.updateGeneralPreferences(this.state.preferences.toUpdateApiModel());
    }

    /**
     * checks the permissions to see if the user has access to adv monitoring
     * Observable completes after call, via {@link hasAnyPermissions}
     */
    hasAdvancedMonitoringAccess(): Observable<boolean> {
        return this.hasAnyPermissions( [userPermissionsEnum.advancedMonitoring] );
    }

    /**
     * checks the permissions to see if the user has access to ghost in
     * Observable completes after call, via {@link hasAnyPermissions}
     */
    hasGhostInAccess(): Observable<boolean> {
        return this.hasAnyPermissions( [userPermissionsEnum.accountGhostIn] );
    }

    /**
     * checks the permissions to see if the user has access to social
     * Observable completes after call, via {@link hasAnyPermissions}
     */
    hasSocialAccess(): Observable<boolean> {
        return this.hasAnyPermissions( [userPermissionsEnum.socialIntelligence] );
    }

    /**
     * checks the permissions to see if the user has access to adv monitoring.
     * Observable completes after call, via {@link hasAnyPermissions}
     */
    hasMonitoringAccess(): Observable<boolean> {
        return this.hasAnyPermissions([
            userPermissionsEnum.advancedMonitoring
        ]);
    }

    /**
     * Checks permissions to see if user them has any of them provisioned. Emits if any permissions are present
     */
    hasAnyPermissions(requiredPermissions: userPermissionsEnum[]): Observable<boolean> {
        return this.getPermissions().pipe(
            filter(x => !!x),
            map(permissions => {
                return requiredPermissions.some(req => permissions.includes(req));
            }),
            take(1)
        );
    }

    /**
     * Checks permissions to see if user all permissions provisioned. Only emits if all permissions are present
     */
    hasAllPermissions(requiredPermissions: userPermissionsEnum[]): Observable<boolean> {
        return this.getPermissions().pipe(
            filter(x => !!x),
            map(permissions => {
                return requiredPermissions.every(req => permissions.includes(req));
            }),
            take(1)
        );
    }

    // @TODO: Remove this, this is a hack to allow greater testing on prod
    accountHaveAiPermissions() {
        return this.getAccount().pipe(
            filter(x => !!x),
            map(account => {
                return this.aiHelperIds.indexOf(account.id) !== -1 || this.customerAiHelperIds.indexOf(account.id) !== -1;
            }),
            take(1)
        );
    }

    accountHasJournodex(account: AccountModel): boolean {
        return account?.permissions.includes(userPermissionsEnum.journodex);
    }

    navigateToJournodex() {
        window.location.href = environment.journodexAppUrl + '/dashboard';
    }

    agenticFeatureEnabled(): boolean {
        return this.state?.account?.permissions?.includes(userPermissionsEnum.agenticFeature);
    }

    /**
     * Whether the account has automation agents, watched rather than read once: on a cold load the
     * account is not in state yet, so this waits for it instead of answering false
     */
    getAgenticFeatureEnabled(): Observable<boolean> {
        return this.getAccount().pipe(
            filter(account => !!account),
            map(account => !!account.permissions?.includes(userPermissionsEnum.agenticFeature))
        );
    }

    debugTopicAgenticWorkflowEnabled(): boolean {
        return environment.debugTopicAgenticWorkflowAccountIds.includes(this.state?.account?.id);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }
}
